import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { HomeIcon, StarIcon, DatabaseIcon, EditIcon, HelpCircleIcon, LayoutLeftIcon, AppLogoIcon, BarChartIcon, LogOutIcon } from "./SidebarIcons";

interface AppSidebarProps {
  className?: string;
}

export function AppSidebar({ className }: AppSidebarProps) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Load collapsed state from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("app-sidebar-collapsed");
    if (stored === "true") {
      setIsCollapsed(true);
    }
  }, []);

  // Save collapsed state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("app-sidebar-collapsed", isCollapsed.toString());
  }, [isCollapsed]);

  // Compute effective expanded state
  const isExpanded = !isCollapsed || isHovered;

  const navItems = [
    {
      title: "Home",
      href: "/",
      Icon: HomeIcon,
    },
    {
      title: "Query Generator",
      href: "/query-generator",
      Icon: StarIcon,
    },
    {
      title: "Analytics",
      href: "/analytics",
      Icon: BarChartIcon,
    },
    {
      title: "Connections",
      href: "/connections",
      Icon: DatabaseIcon,
    },
    {
      title: "Customise Result",
      href: "/instructions",
      Icon: EditIcon,
    },
  ];

  const getUserInitials = (fullName?: string, email?: string) => {
    if (fullName) {
      return fullName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <div
      className={cn(
        "bg-background flex flex-col h-full items-start justify-between pb-[16px] pt-[12px] px-[12px] overflow-hidden",
        isExpanded ? "w-[197px]" : "w-16",
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Top Section */}
      <div className="flex flex-col gap-[20px] items-start w-full pt-[12px]">
        {/* Logo and Collapse Button */}
        <div className="flex items-center justify-between w-full pl-[4px] pr-0">
          <Link
            to="/"
            className="h-[28px] relative flex items-center"
          >
            <div className="shrink-0 w-[26px] h-[26px] flex items-center justify-center">
              <AppLogoIcon className="w-[15px] h-[21px]" />
            </div>
            <span
              className={cn(
                "font-semibold text-[16.077px] text-[#212121] leading-[28px] tracking-[-0.1px] whitespace-nowrap ml-[3px] transition-opacity duration-300",
                isExpanded ? "opacity-100" : "opacity-0 w-0 overflow-hidden",
                "font-['Sora',sans-serif]"
              )}
              style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600 }}
            >
              DytaFly
            </span>
          </Link>
          <button
            className={cn(
              "size-[24px] rounded-[6px] hover:bg-white/50 flex items-center justify-center cursor-pointer transition-all duration-300 p-1",
              isExpanded ? "opacity-100" : "opacity-0 pointer-events-none"
            )}
            onClick={() => {
              setIsCollapsed(!isCollapsed);
              // Reset hover state when collapsing to ensure immediate collapse
              if (!isCollapsed) {
                setIsHovered(false);
              }
            }}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <LayoutLeftIcon className="size-[16px]" />
          </button>
        </div>

        {/* Main Navigation */}
        <nav className="flex flex-col gap-[4px] w-full">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "flex items-center py-[8px] rounded-[8px] transition-colors relative pl-[10px]",
                  isActive
                    ? "bg-white shadow-[0px_1px_2px_0px_rgba(37,33,45,0.11)] rounded-[4px]"
                    : "hover:bg-white/50 rounded-[8px]",
                  isExpanded ? "pr-[10px] gap-[10px]" : "pr-2"
                )}
              >
                <div className="shrink-0 size-[16px] flex items-center justify-center">
                  <item.Icon className="size-[16px]" isActive={isActive} />
                </div>
                <span
                  className={cn(
                    "text-[13px] font-medium text-[#22212d] leading-[16px] tracking-[-0.1px] font-['Geist',sans-serif] whitespace-nowrap transition-opacity duration-300",
                    isExpanded ? "opacity-100" : "opacity-0 w-0 overflow-hidden"
                  )}
                >
                  {item.title}
                </span>
                {isActive && (
                  <div className="absolute inset-0 pointer-events-none shadow-[inset_0px_1px_2px_0px_rgba(255,255,255,0.32),inset_0px_-1px_3px_0px_rgba(51,60,79,0.06)] rounded-[4px]" />
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Bottom Section */}
      <div className="flex flex-col gap-[8px] w-full">
        {/* Help & Support */}
        <Link
          to="/help"
          className={cn(
            "flex items-center py-[8px] rounded-[8px] transition-colors pl-[10px]",
            location.pathname === "/help"
              ? "bg-white shadow-[0px_1px_2px_0px_rgba(37,33,45,0.11)] rounded-[4px]"
              : "hover:bg-white/50 rounded-[8px]",
            isExpanded ? "pr-[10px] gap-[10px]" : "pr-2"
          )}
        >
          <div className="shrink-0 size-[16px] flex items-center justify-center">
            <HelpCircleIcon className="size-[16px]" />
          </div>
          <span
            className={cn(
              "text-[13px] font-medium text-[#22212d] leading-[16px] tracking-[-0.1px] font-['Geist',sans-serif] whitespace-nowrap transition-opacity duration-300",
              isExpanded ? "opacity-100" : "opacity-0 w-0 overflow-hidden"
            )}
          >
            Help & Support
          </span>
        </Link>

        {/* Logout */}
        <button
          onClick={() => logout()}
          className={cn(
            "flex items-center py-[8px] rounded-[8px] transition-colors pl-[10px] w-full text-left hover:bg-white/50 hover:text-red-600 group",
            isExpanded ? "pr-[10px] gap-[10px]" : "pr-2"
          )}
        >
          <div className="shrink-0 size-[16px] flex items-center justify-center">
            <LogOutIcon className="size-[16px] group-hover:text-red-600 transition-colors" />
          </div>
          <span
            className={cn(
              "text-[13px] font-medium text-[#22212d] group-hover:text-red-600 leading-[16px] tracking-[-0.1px] font-['Geist',sans-serif] whitespace-nowrap transition-opacity duration-300",
              isExpanded ? "opacity-100" : "opacity-0 w-0 overflow-hidden"
            )}
          >
            Log Out
          </span>
        </button>

        {/* User Profile */}
        {user && (
          <div className="flex items-center gap-[6px] pl-[10px] py-[6px] rounded-[6px] mt-2 border-t pt-3">
            <Avatar className="size-[24px] rounded-full bg-[#888] shrink-0">
              <AvatarFallback className="text-[10px] text-white bg-[#888]">
                {getUserInitials(user.full_name, user.email)}
              </AvatarFallback>
            </Avatar>
            <div className={cn(
              "flex flex-col transition-opacity duration-300",
              isExpanded ? "opacity-100" : "opacity-0 w-0 overflow-hidden"
            )}>
              <span className="text-[13px] font-medium text-[#22212d] leading-[16px] tracking-[-0.1px] font-['Geist',sans-serif] whitespace-nowrap">
                {user.full_name || "User"}
              </span>
              <span className="text-[10px] text-muted-foreground truncate max-w-[140px]">
                {user.email}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AppSidebar;
